/* global globalThis */
import { getAuthorizationToken, getIdentityAssertion } from '@/apps/apis/claudeCodeDashboard/token';
import { type DpPriceRow } from '@/infrastructure/CompetitorV2Client';

export class StaffDpPriceApiError extends Error {
  public constructor(
    public readonly status: number,
    /** The server's error message, when it sent one. */
    public readonly code?: string,
  ) {
    super(`Staff price request failed (${status})`);
  }
}

export interface StaffDpPriceAction {
  decision: 'APPROVED' | 'REJECTED';
  regularPrice?: string;
  salePrice?: string;
  note?: string;
}

/** Approves (publishes to the store) or rejects one SKU's DP price row through the internal API. */
export class StaffDpPriceClient {
  private readonly request: typeof fetch;

  public constructor(
    private readonly apiUrl: string,
    private readonly apiKey: string,
    request?: typeof fetch,
  ) {
    this.request = request ?? ((input, init) => globalThis.fetch(input, init));
  }

  public async act(targetSku: string, body: StaffDpPriceAction): Promise<DpPriceRow> {
    const token = await getAuthorizationToken();
    if (!token) {
      throw new StaffDpPriceApiError(401);
    }
    const assertion = await getIdentityAssertion();
    const response = await this.request(
      `${this.apiUrl.replace(/\/+$/, '')}/products/prices/dpReviews/${encodeURIComponent(targetSku)}/actions`,
      {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'X-API-Key': this.apiKey,
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...(assertion ? { 'x-identity-assertion': assertion } : {}),
        },
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      const errorBody: unknown = await response.json().catch(() => null);
      const message = errorBody && typeof errorBody === 'object' && 'message' in errorBody ? errorBody.message : null;
      throw new StaffDpPriceApiError(response.status, typeof message === 'string' ? message : undefined);
    }
    return response.json() as Promise<DpPriceRow>;
  }
}
